import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { DayPicker } from "react-day-picker";
import "react-day-picker/style.css";
import styles from "./Sandbox.module.css";

/* ------------------------------------------------------------------ */
/*  Mock Data                                                          */
/* ------------------------------------------------------------------ */

const MOCK_DATES = ["03/16", "03/17", "03/18", "03/19", "03/20", "03/21", "03/22", "03/23", "03/24", "03/25"];

const MOCK_CLIENTS = ["Clearwater Energy", "Basin Resources", "Summit Pipeline", "Horizon Land Co."];

const MOCK_PROSPECTS = ["Clearwater Energy #2847", "Basin Resources #1923", "Summit Pipeline #4102"];

const MOCK_LEGALS = ["SEC 14 T2N R5E", "SEC 22 T3N R4W", "ABS 142 BLK C"];

const MOCK_JOBS = ["Title Search", "Curative", "Leasing", "ROW Acquisition", "Due Diligence"];

const MOCK_DAYS = ["0.25", "0.50", "0.75", "1.00"];

const MOCK_USERS = ["John Smith", "Sarah Johnson", "Mike Williams", "Emily Davis", "Chris Martinez"];

const MOCK_EXPENSE_ACCOUNTS = ["Field Operations", "Corporate Overhead", "Equipment & Supplies", "Travel & Lodging"];

const MOCK_ENTRIES = [
  { id: 1, date: "03/17", client: "Clearwater Energy", prospect: "Clearwater Energy #2847", legal: "SEC 14 T2N R5E", job: "Title Search", days: "1.00", description: "Reviewed title documents and prepared runsheet", status: "pending" as const, hasExpenses: true, denialReason: "" },
  { id: 2, date: "03/18", client: "Basin Resources", prospect: "Basin Resources #1923", legal: "SEC 22 T3N R4W", job: "Curative", days: "0.75", description: "Resolved outstanding liens on mineral rights", status: "invoiced" as const, hasExpenses: true, denialReason: "" },
  { id: 3, date: "03/19", client: "Clearwater Energy", prospect: "Clearwater Energy #2847", legal: "ABS 142 BLK C", job: "Leasing", days: "0.50", description: "Negotiated lease terms with landowner", status: "pending" as const, hasExpenses: false, denialReason: "" },
  { id: 4, date: "03/20", client: "Summit Pipeline", prospect: "Summit Pipeline #4102", legal: "SEC 14 T2N R5E", job: "ROW Acquisition", days: "1.00", description: "Surveyed right-of-way corridor and met with property owners", status: "rejected" as const, hasExpenses: true, denialReason: "Incorrect job code selected — this work should be billed under Due Diligence, not ROW Acquisition. Please resubmit with the correct job type." },
  { id: 5, date: "03/21", client: "Basin Resources", prospect: "Basin Resources #1923", legal: "SEC 22 T3N R4W", job: "Due Diligence", days: "0.25", description: "Verified ownership records at county courthouse", status: "invoiced" as const, hasExpenses: false, denialReason: "" },
  { id: 6, date: "03/22", client: "Horizon Land Co.", prospect: "Clearwater Energy #2847", legal: "SEC 14 T2N R5E", job: "Title Search", days: "0.50", description: "Follow-up review of abstract chain", status: "pending" as const, hasExpenses: false, denialReason: "" },
];

/* -- SVG Icons (matching TimeEntry premium style) -- */
function ReceiptIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M6.667 2.5v2.083M10 2.5v2.083M13.333 2.5v2.083M5 4.583h10a1.667 1.667 0 0 1 1.667 1.667v9.583l-1.667-.833-1.667.833-1.666-.833-1.667.833-1.667-.833-1.666.833-1.667-.833-1.667.833V6.25A1.667 1.667 0 0 1 5 4.583Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7.083 8.333h5.834M7.083 11.25h4.167" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function SaveIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M4.167 4.167h9.166l2.5 2.5v8.75A1.25 1.25 0 0 1 14.583 16.667H5.417a1.25 1.25 0 0 1-1.25-1.25V5.417a1.25 1.25 0 0 1 1.25-1.25Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M6.667 4.167v4.166h5V4.167M6.667 16.667v-4.584h6.666v4.584" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M5.833 6.667v8.75A1.25 1.25 0 0 0 7.083 16.667h5.834a1.25 1.25 0 0 0 1.25-1.25v-8.75M4.167 5h11.666M7.5 5V3.75c0-.69.56-1.25 1.25-1.25h2.5c.69 0 1.25.56 1.25 1.25V5M8.333 9.167v4.166M11.667 9.167v4.166" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M10 4.167v11.666M4.167 10h11.666" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function ArrowLeftIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M12.5 4.167L6.667 10l5.833 5.833" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M7.5 4.167L13.333 10 7.5 15.833" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Tab 1 — Structured + Dashboard Hybrid                             */
/* ------------------------------------------------------------------ */
const HybridDesign: React.FC = () => {
  const [expenseModalOpen, setExpenseModalOpen] = useState(false);
  const [expenseEntryId, setExpenseEntryId] = useState<number | null>(null);
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);
  const [entryMode, setEntryMode] = useState<"my" | "user" | "expense">("my");
  const [showEmptyState, setShowEmptyState] = useState(false);

  /* ---- Calendar state (mirrors TimeEntry exactly) ---- */
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false,
  );
  const [visibleMonth, setVisibleMonth] = useState(new Date(2026, 2));
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0, width: 340, ready: false });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const openExpenseModal = (id: number) => { setExpenseEntryId(id); setExpenseModalOpen(true); };
  const closeExpenseModal = () => { setExpenseModalOpen(false); setExpenseEntryId(null); };
  const activeExpenseEntry = MOCK_ENTRIES.find((e) => e.id === expenseEntryId);

  /* Mobile detection */
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* Popover positioning */
  const updatePopoverPosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const popoverWidth = 340;
    const left = Math.min(
      Math.max(12, rect.right - popoverWidth),
      window.innerWidth - popoverWidth - 12,
    );
    setPopoverPos({ top: rect.bottom + 8, left, width: popoverWidth, ready: true });
  };

  useLayoutEffect(() => {
    if (!isCalendarOpen || isMobile) return;
    updatePopoverPosition();
    const handleReposition = () => updatePopoverPosition();
    window.addEventListener("resize", handleReposition);
    document.addEventListener("scroll", handleReposition, true);
    return () => {
      window.removeEventListener("resize", handleReposition);
      document.removeEventListener("scroll", handleReposition, true);
    };
  }, [isCalendarOpen, isMobile]);

  /* Outside click */
  useEffect(() => {
    if (!isCalendarOpen) return;
    const handleOutside = (event: any) => {
      if (triggerRef.current?.contains(event.target)) return;
      if (popoverRef.current?.contains(event.target)) return;
      setIsCalendarOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [isCalendarOpen]);

  const openPicker = () => {
    setVisibleMonth(new Date(2026, 2));
    if (!isMobile) {
      setPopoverPos((prev) => ({ ...prev, ready: false }));
      updatePopoverPosition();
    } else {
      setPopoverPos((prev) => ({ ...prev, ready: true }));
    }
    setIsCalendarOpen(true);
  };

  const handleDayClick = (day: Date) => {
    setIsCalendarOpen(false);
  };

  const calendarPopover = (
    <div
      ref={popoverRef}
      className={`${styles["hybrid-rdp-wrap"]} ${isMobile ? styles["hybrid-mobile-calendar-card"] : styles["hybrid-calendar-card"]}`}
      style={
        isMobile
          ? undefined
          : { top: popoverPos.top, left: popoverPos.left, width: popoverPos.width, visibility: popoverPos.ready ? "visible" as const : "hidden" as const }
      }
    >
      {isMobile && (
        <div className={styles["hybrid-mobile-calendar-header"]}>
          <div className={styles["hybrid-mobile-calendar-title"]}>Select Billing Period</div>
          <button type="button" onClick={() => setIsCalendarOpen(false)} className={styles["hybrid-mobile-calendar-close"]}>Close</button>
        </div>
      )}
      <div className={styles["hybrid-calendar-hint"]}>
        Pick any date to load the first or second half of that month.
      </div>
      <DayPicker
        mode="single"
        numberOfMonths={1}
        month={visibleMonth}
        onMonthChange={setVisibleMonth}
        onDayClick={handleDayClick}
        showOutsideDays
      />
    </div>
  );

  return (
    <div className={styles["hybrid-container"]}>
      {/* B. Context Switcher + Period Bar */}
      <div className={styles["hybrid-card"]}>
        <div className={styles["hybrid-card-header"]}>
          <h2 className={styles["hybrid-card-title"]}>Billing Period</h2>
        </div>
        <div className={styles["hybrid-card-body"]}>
          <div className={styles["hybrid-context-row"]}>
            <div className={styles["hybrid-context-tabs"]}>
              <button className={`${styles["hybrid-pill"]} ${entryMode === "my" ? styles["hybrid-pill--active"] : ""}`} type="button" onClick={() => setEntryMode("my")}>My Time</button>
              <button className={`${styles["hybrid-pill"]} ${entryMode === "user" ? styles["hybrid-pill--active"] : ""}`} type="button" onClick={() => setEntryMode("user")}>User</button>
              <button className={`${styles["hybrid-pill"]} ${entryMode === "expense" ? styles["hybrid-pill--active"] : ""}`} type="button" onClick={() => setEntryMode("expense")}>Company Expense</button>
            </div>
            {entryMode === "user" && (
              <div className={styles["hybrid-context-picker"]}>
                <select className={styles["hybrid-select"]}>
                  <option value="">Select User</option>
                  {MOCK_USERS.map((u) => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>
              </div>
            )}
            {entryMode === "expense" && (
              <div className={styles["hybrid-context-picker"]}>
                <select className={styles["hybrid-select"]}>
                  <option value="">Select Company Expense Account</option>
                  {MOCK_EXPENSE_ACCOUNTS.map((a) => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className={styles["hybrid-period-group"]}>
            <div className={styles["hybrid-period-row"]}>
              <button className={styles["hybrid-arrow-btn"]} type="button"><ArrowLeftIcon /></button>
              <button
                ref={triggerRef}
                type="button"
                className={styles["hybrid-period-label"]}
                onClick={openPicker}
                aria-haspopup="dialog"
                aria-expanded={isCalendarOpen}
              >
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true" style={{ marginRight: 6, flexShrink: 0 }}>
                  <rect x="3" y="4" width="14" height="13" rx="2" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M3 8h14M7 2v4M13 2v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                Mar 16 – Mar 31, 2026
              </button>
              <button className={styles["hybrid-arrow-btn"]} type="button"><ArrowRightIcon /></button>
            </div>
            <div className={styles["hybrid-period-hint"]}>Semi-monthly · 16 days remaining</div>
          </div>
        </div>
      </div>

      {/* D. Add Entry Composer */}
      <div className={styles["hybrid-card"]}>
        <div className={styles["hybrid-card-header"]}>
          <h2 className={styles["hybrid-card-title"]}>New Entry</h2>
        </div>
        <div className={styles["hybrid-card-body"]}>
          <div className={styles["hybrid-composer-row1"]}>
            <div className={styles["hybrid-field"]}>
              <label className={styles["hybrid-label"]}>Date</label>
              <select className={styles["hybrid-select"]} defaultValue="03/17">
                {MOCK_DATES.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
            <div className={styles["hybrid-field"]}>
              <label className={styles["hybrid-label"]}>Client</label>
              <select className={styles["hybrid-select"]}>
                <option value="">Select client…</option>
                {MOCK_CLIENTS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className={styles["hybrid-field"]}>
              <label className={styles["hybrid-label"]}>Prospect</label>
              <select className={styles["hybrid-select"]}>
                <option value="">Select prospect…</option>
                {MOCK_PROSPECTS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div className={styles["hybrid-field"]}>
              <label className={styles["hybrid-label"]}>Legal</label>
              <select className={styles["hybrid-select"]}>
                <option value="">Select legal…</option>
                {MOCK_LEGALS.map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            </div>
          </div>
          <div className={styles["hybrid-composer-row2"]}>
            <div className={styles["hybrid-field"]}>
              <label className={styles["hybrid-label"]}>Job</label>
              <select className={styles["hybrid-select"]}>
                <option value="">Select job…</option>
                {MOCK_JOBS.map((j) => (
                  <option key={j} value={j}>{j}</option>
                ))}
              </select>
            </div>
            <div className={styles["hybrid-field"]}>
              <label className={styles["hybrid-label"]}>Days</label>
              <select className={styles["hybrid-select"]}>
                {MOCK_DAYS.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
            <div className={styles["hybrid-field"]}>
              <label className={styles["hybrid-label"]}>Description</label>
              <input type="text" className={styles["hybrid-input"]} placeholder="Enter description…" />
            </div>
            <div className={styles["hybrid-field"]}>
              <label className={styles["hybrid-label"]}>&nbsp;</label>
              <div className={styles["hybrid-composer-actions"]}>
                <button
                  className={styles["hybrid-expense-btn"]}
                  type="button"
                  title="Expenses"
                  onClick={() => openExpenseModal(0)}
                >
                  <ReceiptIcon />
                </button>
                <button className={styles["hybrid-add-btn"]} type="button"><PlusIcon /> Add Entry</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* E. Submitted Entries */}
      <div className={styles["hybrid-card"]}>
        <div className={styles["hybrid-card-header"]}>
          <h2 className={styles["hybrid-card-title"]}>
            Submitted Entries
            <span
              className={styles["hybrid-count-badge"]}
              onClick={() => setShowEmptyState(!showEmptyState)}
              style={{ cursor: "pointer" }}
              title="Toggle empty state preview"
            >
              {showEmptyState ? "0 entries" : "6 entries"}
            </span>
          </h2>
          {!showEmptyState && (
            <div className={styles["hybrid-legend"]}>
              <span className={styles["hybrid-legend-item"]}>
                <span className={`${styles["hybrid-legend-dot"]} ${styles["hybrid-legend-dot--pending"]}`} />
                Pending
              </span>
              <span className={styles["hybrid-legend-item"]}>
                <span className={`${styles["hybrid-legend-dot"]} ${styles["hybrid-legend-dot--invoiced"]}`} />
                Invoiced
              </span>
              <span className={styles["hybrid-legend-item"]}>
                <span className={`${styles["hybrid-legend-dot"]} ${styles["hybrid-legend-dot--denied"]}`} />
                Rejected
              </span>
            </div>
          )}
        </div>
        <div className={styles["hybrid-card-body-flush"]}>
          {showEmptyState ? (
            <div className={styles["hybrid-empty-state"]}>
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden="true">
                <rect x="8" y="10" width="32" height="30" rx="4" stroke="#d1d5db" strokeWidth="2" />
                <path d="M8 18h32" stroke="#d1d5db" strokeWidth="2" />
                <path d="M16 6v8M32 6v8" stroke="#d1d5db" strokeWidth="2" strokeLinecap="round" />
                <circle cx="24" cy="30" r="4" stroke="#d1d5db" strokeWidth="2" />
              </svg>
              <p className={styles["hybrid-empty-title"]}>No entries yet</p>
              <p className={styles["hybrid-empty-subtitle"]}>Use the form above to add your first time entry for this billing period.</p>
            </div>
          ) : (
            <>
              {/* Desktop grid header */}
              <div className={`${styles["hybrid-grid-row"]} ${styles["hybrid-grid-header"]}`}>
                <span>Date</span>
                <span>Client</span>
                <span>Prospect</span>
                <span>Legal</span>
                <span>Job</span>
                <span className={styles["hybrid-cell-right"]}>Days</span>
                <span>Actions</span>
              </div>

              {/* Desktop grid rows */}
              {MOCK_ENTRIES.map((e) => (
                <div
                  key={e.id}
                  className={`${styles["hybrid-entry-wrapper"]} ${styles[`hybrid-row-${e.status === "rejected" ? "denied" : e.status}`]} ${e.status === "rejected" ? styles["hybrid-row-clickable"] : ""}`}
                  onClick={e.status === "rejected" && e.denialReason ? () => setExpandedRowId(expandedRowId === e.id ? null : e.id) : undefined}
                >
                  <div className={`${styles["hybrid-grid-row"]} ${styles["hybrid-grid-data"]}`}>
                    <span>{e.date}</span>
                    <span>{e.client}</span>
                    <span>{e.prospect}</span>
                    <span>{e.legal}</span>
                    <span>{e.job}</span>
                    <span className={`${styles["hybrid-cell-right"]} ${styles["hybrid-cell-days"]}`}>{e.days}</span>
                    <span className={styles["hybrid-cell-actions"]}>
                      <button
                        className={styles["hybrid-row-action-btn"]}
                        type="button"
                        title="Expenses"
                        onClick={(ev) => { ev.stopPropagation(); openExpenseModal(e.id); }}
                      >
                        <ReceiptIcon />
                        {e.hasExpenses && <span className={styles["hybrid-expense-dot"]} />}
                      </button>
                      {e.status === "pending" && <button className={styles["hybrid-row-action-btn"]} type="button" title="Save" onClick={(ev) => ev.stopPropagation()}><SaveIcon /></button>}
                      {e.status !== "invoiced" && <button className={`${styles["hybrid-row-action-btn"]} ${styles["hybrid-row-action-btn--danger"]}`} type="button" title="Delete" onClick={(ev) => ev.stopPropagation()}><TrashIcon /></button>}
                    </span>
                  </div>
                  {e.description && (
                    <div className={styles["hybrid-description-line"]}>{e.description}</div>
                  )}
                  {e.status === "rejected" && e.denialReason && expandedRowId === e.id && (
                    <div className={styles["hybrid-denial-reason"]}>
                      <strong>Denial Reason:</strong> {e.denialReason}
                    </div>
                  )}
                </div>
              ))}

              {/* Mobile stacked cards */}
              {MOCK_ENTRIES.map((e) => (
                <div
                  key={`m-${e.id}`}
                  className={`${styles["hybrid-entry-card"]} ${styles[`hybrid-row-${e.status}`]}`}
                >
                  <div className={styles["hybrid-entry-card-row"]}>
                    <span className={styles["hybrid-entry-card-label"]}>Date</span>
                    <span>{e.date}</span>
                  </div>
                  <div className={styles["hybrid-entry-card-row"]}>
                    <span className={styles["hybrid-entry-card-label"]}>Client</span>
                    <span>{e.client}</span>
                  </div>
                  <div className={styles["hybrid-entry-card-row"]}>
                    <span className={styles["hybrid-entry-card-label"]}>Prospect</span>
                    <span>{e.prospect}</span>
                  </div>
                  <div className={styles["hybrid-entry-card-row"]}>
                    <span className={styles["hybrid-entry-card-label"]}>Legal</span>
                    <span>{e.legal}</span>
                  </div>
                  <div className={styles["hybrid-entry-card-row"]}>
                    <span className={styles["hybrid-entry-card-label"]}>Job</span>
                    <span>{e.job}</span>
                  </div>
                  <div className={styles["hybrid-entry-card-row"]}>
                    <span className={styles["hybrid-entry-card-label"]}>Days</span>
                    <span className={styles["hybrid-cell-days"]}>{e.days}</span>
                  </div>
                  {e.description && (
                    <div className={styles["hybrid-entry-card-row"]}>
                      <span className={styles["hybrid-entry-card-label"]}>Description</span>
                      <span>{e.description}</span>
                    </div>
                  )}
                  <div className={styles["hybrid-entry-card-actions"]}>
                    <button
                      className={styles["hybrid-row-action-btn"]}
                      type="button"
                      title="Expenses"
                      onClick={() => openExpenseModal(e.id)}
                    >
                      <ReceiptIcon />
                      {e.hasExpenses && <span className={styles["hybrid-expense-dot"]} />}
                    </button>
                    {e.status === "pending" && <button className={styles["hybrid-row-action-btn"]} type="button" title="Save"><SaveIcon /></button>}
                    {e.status !== "invoiced" && <button className={`${styles["hybrid-row-action-btn"]} ${styles["hybrid-row-action-btn--danger"]}`} type="button" title="Delete"><TrashIcon /></button>}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Calendar Popup (portalled to body — same pattern as TimeEntry) */}
      {isCalendarOpen &&
        typeof document !== "undefined" &&
        createPortal(
          isMobile ? (
            <div className={styles["hybrid-mobile-calendar-backdrop"]}>{calendarPopover}</div>
          ) : (
            calendarPopover
          ),
          document.body,
        )}

      {/* Expense Modal */}
      {expenseModalOpen && (
        <div className={styles["expense-overlay"]} onClick={closeExpenseModal}>
          <div
            className={`${styles["expense-modal"]} ${styles["hybrid-expense-modal"]}`}
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className={styles["expense-modal-header"]}>
              <h3 className={styles["expense-modal-title"]}>Expenses</h3>
              <button className={styles["expense-close-btn"]} type="button" onClick={closeExpenseModal}>×</button>
            </div>
            <div className={styles["expense-fields"]}>
              <div className={styles["expense-field"]}>
                <label className={styles["expense-label"]}>Miles</label>
                <input
                  type="text"
                  className={styles["expense-input"]}
                  defaultValue={activeExpenseEntry?.hasExpenses ? "47" : ""}
                  placeholder="0"
                />
              </div>
              <div className={styles["expense-field"]}>
                <label className={styles["expense-label"]}>Meals</label>
                <input
                  type="text"
                  className={styles["expense-input"]}
                  defaultValue={activeExpenseEntry?.hasExpenses ? "12.50" : ""}
                  placeholder="0.00"
                />
              </div>
              <div className={styles["expense-field"]}>
                <label className={styles["expense-label"]}>Misc</label>
                <input
                  type="text"
                  className={styles["expense-input"]}
                  defaultValue={activeExpenseEntry?.hasExpenses ? "8.75" : ""}
                  placeholder="0.00"
                />
              </div>
            </div>
            <label className={styles["expense-upload"]}>
              📎 Drop receipts here or click to upload
              <span className={styles["expense-upload-text"]}>PDF, PNG, or JPG up to 10 MB</span>
              <input type="file" multiple />
            </label>
            <div className={styles["expense-footer"]}>
              <button className={styles["expense-cancel-btn"]} type="button" onClick={closeExpenseModal}>Cancel</button>
              <button className={styles["expense-save-btn"]} type="button" onClick={closeExpenseModal}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Main Sandbox component                                            */
/* ------------------------------------------------------------------ */
const Sandbox: React.FC = () => {
  return (
    <div className="main-articles">
      <div className={styles["sandbox-shell"]}>
        <HybridDesign />
      </div>
    </div>
  );
};

export default Sandbox;
